import React from 'react';

/**
 * ElencoDomande — i campi di un questionario o di una scheda.
 *
 * PERCHE' STA DA SOLA
 * La usano due schermate: il tablet, dove risponde il paziente, e la
 * postazione, dove il medico registra. Scriverla due volte vorrebbe dire
 * che prima o poi una delle due mostra le opzioni in un ordine diverso, o
 * smette di segnare i campi saltati - e la differenza la scopre chi
 * confronta due compilazioni dello stesso paziente.
 *
 * I TIPI
 * `scala` e `scelta` sono a risposta chiusa e si mostrano a pulsanti: su
 * un tablet un menu a tendina si apre male e su una scheda clinica un
 * elenco visibile fa vedere tutte le possibilita' senza cercarle.
 * `numero`, `data`, `testo` e `testo_lungo` sono campi, con l'unita' di
 * misura accanto quando c'e': "Hb 11" non dice niente, "Hb 11 g/dL" si'.
 *
 * I CAMPI CHE VENGONO DALL'ANAGRAFICA NON SI SCRIVONO
 * Nome, data di nascita, sesso, codice fiscale li ha gia' la scheda. Si
 * mostrano compilati e non modificabili: farli ribattere e' il modo
 * classico di ritrovarsi due date di nascita per la stessa persona, e
 * chi corregge qui non correggerebbe comunque l'anagrafica.
 */

/**
 * Un'opzione con la sua figura, quando c'e'.
 *
 * L'immagine si nasconde da sola se il file non c'e': le figure della
 * scala di Bristol sono materiale della clinica e stanno fuori dal
 * repository, quindi su un'installazione appena fatta non ci sono. Una
 * cornice rotta accanto a una domanda clinica e' peggio della sola
 * descrizione, e chi guarda non deve chiedersi se e' il programma a
 * essere rotto.
 */
const Opzione = ({ o, scelta, onClick }) => {
    const [senzaFigura, setSenzaFigura] = React.useState(false);
    return (
        <button type="button" className={`qt-opz ${scelta ? 'scelta' : ''}`} onClick={onClick}>
            {o.immagine && !senzaFigura && (
                <img className="qt-opz-figura" src={o.immagine} alt=""
                     onError={() => setSenzaFigura(true)} />
            )}
            <span className="qt-opz-testo">
                {o.etichetta}
                {o.descrizione && <span className="qt-opz-nota">{o.descrizione}</span>}
            </span>
        </button>
    );
};

const Chiuso = ({ item, opzioni, scelto, onRispondi }) => (
    <div className={`qt-opzioni ${item.scala_bristol ? 'bristol' : ''}`}>
        {opzioni.map((o) => (
            <Opzione key={o.valore} o={o}
                     scelta={String(scelto) === String(o.valore)}
                     onClick={() => onRispondi(item.n, scelto === o.valore ? '' : o.valore)} />
        ))}
    </div>
);

const ElencoDomande = ({ item, scale, risposte, onRispondi, mancanti = [], anagrafica = {} }) => {
    const opzioniDi = (it) => (
        Array.isArray(it.opzioni) ? it.opzioni : (scale.get(it.scala)?.opzioni || [])
    );
    const tipoDi = (it) => it.tipo || (it.scala ? 'scala' : 'testo');

    let gruppoPrec = null;

    return (
        <>
            {item.map((it) => {
                const tipo = tipoDi(it);
                const valore = risposte[String(it.n)];
                const daAnagrafica = it.da_anagrafica
                    ? anagrafica[it.da_anagrafica] ?? null : null;
                const bloccato = it.da_anagrafica !== undefined || it.calcolato !== undefined;

                // Le intestazioni di gruppo (Emocromo, Ferro, Fegato…)
                const testa = it.gruppo && it.gruppo !== gruppoPrec
                    ? <div className="qt-gruppo" key={`g${it.n}`}>{it.gruppo}</div> : null;
                gruppoPrec = it.gruppo || null;

                return (
                    <React.Fragment key={it.n}>
                        {testa}
                        <div className={`qt-item ${mancanti.includes(it.n) ? 'manca' : ''}`
                                        + (bloccato ? ' bloccato' : '')
                                        + (tipo === 'testo' || tipo === 'numero' || tipo === 'data'
                                            ? ' in-riga' : '')}>
                            <div className="qt-domanda">
                                {tipo === 'scala' && <span className="qt-n">{it.n}</span>}
                                <span className="qt-etichetta">{it.testo}</span>
                                {it.nota && <span className="qt-nota-campo">{it.nota}</span>}
                            </div>

                            {bloccato ? (
                                <div className="qt-fisso">
                                    {daAnagrafica ?? (valore ?? '—')}
                                    {it.unita && <span className="qt-um"> {it.unita}</span>}
                                    <span className="qt-provenienza">
                                        {it.calcolato ? 'calcolato' : 'dalla scheda paziente'}
                                    </span>
                                </div>
                            ) : tipo === 'scala' || tipo === 'scelta' ? (
                                <Chiuso item={it} opzioni={opzioniDi(it)}
                                        scelto={valore} onRispondi={onRispondi} />
                            ) : tipo === 'testo_lungo' ? (
                                <textarea className="qt-campo qt-campo--lungo" rows={3}
                                          value={valore ?? ''}
                                          onChange={(e) => onRispondi(it.n, e.target.value)} />
                            ) : tipo === 'data' ? (
                                <input type="date" className="qt-campo qt-campo--data"
                                       value={valore ?? ''}
                                       onChange={(e) => onRispondi(it.n, e.target.value)} />
                            ) : tipo === 'numero' ? (
                                <span className="qt-numero">
                                    <input type="number" className="qt-campo qt-campo--numero"
                                           inputMode="decimal"
                                           step={it.decimali ? 1 / 10 ** it.decimali : 1}
                                           min={it.min} max={it.max}
                                           value={valore ?? ''}
                                           onChange={(e) => onRispondi(it.n, e.target.value)} />
                                    {it.unita && (
                                        <span className={`qt-um ${it.unita.startsWith('/') ? 'attaccata' : ''}`}>
                                            {it.unita}
                                        </span>
                                    )}
                                </span>
                            ) : (
                                <input type="text" className="qt-campo"
                                       value={valore ?? ''}
                                       onChange={(e) => onRispondi(it.n, e.target.value)} />
                            )}
                        </div>
                    </React.Fragment>
                );
            })}
        </>
    );
};

export default ElencoDomande;
